import { useCallback, useEffect, useRef, useState, type ChangeEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Button, EmptyState, Icon } from '@astral/ui';
import {
  acceptForType,
  assignSequenceToAssets,
  classifyMediaFailure,
  DEFAULT_STICKER_EMOJI,
  kindForMime,
  mediaFailureCopyKey,
  MEDIA_RETRYING_COPY_KEY,
  setAssetEmojis,
  type ProjectEmojiPreset,
  type StickerAsset,
  type StickerType,
} from '@astral/shared';
import { hapticImpact, hapticSelection } from '@astral/telegram/webapp';
import { api, ApiClientError } from '../lib/api.js';
import { moveItem, nearestTileIndex, type TileRect } from '../lib/grid-reorder.js';
import {
  DEFAULT_BACKOFF_FACTOR,
  DEFAULT_BASE_DELAY_MS,
  DEFAULT_MAX_ATTEMPTS,
  DEFAULT_MAX_DELAY_MS,
  DEFAULT_UPLOAD_CONCURRENCY,
  isSettled,
  UploadQueue,
  type UploadItem,
} from '../lib/upload-queue.js';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { StickerActionSheet } from './StickerActionSheet.js';
import { BulkEmojiEditor } from './BulkEmojiEditor.js';
import { EmojiPresetManager } from './EmojiPresetManager.js';

export interface StickerGridProps {
  assets: StickerAsset[];
  onChange: (assets: StickerAsset[]) => void;
  stickerType: StickerType;
  limit: number;
  /** Project-level default emoji preset (drives batch auto-apply). */
  projectPreset?: ProjectEmojiPreset | null;
  /**
   * Called when a sticker's media fails to load — typically an expired signed
   * URL. The owner re-mints URLs; this component never builds them itself.
   */
  onMediaError?: () => void;
}

function reindex(assets: StickerAsset[]): StickerAsset[] {
  return assets.map((asset, index) => ({ ...asset, order: index }));
}

const HOLD_MS = 170;
const MOVE_CANCEL_PX = 12;

/** Distance from a viewport edge at which the page starts following the drag. */
const EDGE_PX = 72;
/** Fastest auto-scroll step per frame, at the very edge. */
const EDGE_MAX_STEP = 18;
/** Below this (px/ms) a released swipe is a stop, not a fling. */
const FLING_MIN_VELOCITY = 0.04;
/** Per-millisecond velocity decay of a fling — tuned to feel like the platform. */
const FLING_DECAY = 0.995;

/*
 * Batch upload concurrency.
 *
 * Images run through the bounded queue at {@link DEFAULT_UPLOAD_CONCURRENCY};
 * video (FFmpeg) stays strictly serial, because a transcode is an order of
 * magnitude more expensive than an image encode and the backend's own ffmpeg
 * queue is narrower than its image queue.
 */
const VID_CONCURRENCY = 1;

/** What one queued upload turns into once it succeeds. */
type Draft = Omit<StickerAsset, 'order'>;

/**
 * The element that actually scrolls above `node`, or null for the document.
 *
 * Since the tiles hand no gesture to the browser, this component has to move
 * the page itself — and it must move the RIGHT thing. The editor scrolls the
 * document today, but a Mini App host that wraps the app in its own scrolling
 * container would leave `window.scrollBy` doing nothing at all, which would
 * read as a frozen editor.
 */
function scrollableAncestor(node: HTMLElement | null): HTMLElement | null {
  let el: HTMLElement | null = node?.parentElement ?? null;
  while (el && el !== document.body && el !== document.documentElement) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * What the gesture has turned out to be.
 *
 * WebKit decides who owns a touch when it BEGINS and never hands ownership
 * over mid-gesture, so the tiles keep `touch-action: none` and this component
 * owns every touch that starts on one from the first event. That makes the
 * three outcomes ours to tell apart:
 *
 *   pending   — finger down, intent unknown (a tap, a scroll or a drag)
 *   scrolling — moved before the hold elapsed: the page is scrolled by hand,
 *               because the browser will not do it for us on this surface
 *   dragging  — held still long enough: a full 2D reorder
 */
type GesturePhase = 'pending' | 'scrolling' | 'dragging';

interface DragSession {
  id: string;
  index: number;
  /** Pointer id for mouse/pen; null when the gesture came from a touch. */
  pointerId: number | null;
  startX: number;
  startY: number;
  /** Last seen finger position, for hand-rolled scrolling. */
  lastY: number;
  lastT: number;
  /** Scroll velocity in px/ms, for the release fling. */
  vy: number;
  grabDx: number;
  grabDy: number;
  w: number;
  h: number;
  phase: GesturePhase;
}

/**
 * Sticker asset manager. Files are converted by the backend pipeline, then
 * previewed against the theme surface (transparency preserved in the stored
 * PNG — only the preview background changes). Reordering is touch-friendly
 * drag-and-drop (press & hold), persisted via the parent's autosave.
 */
export function StickerGrid({
  assets,
  onChange,
  stickerType,
  limit,
  projectPreset,
  onMediaError,
}: StickerGridProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  /*
   * The grid NODE, as state rather than only a ref: the touch listeners must be
   * bound to it for its whole lifetime (see the gesture section), and an effect
   * cannot depend on a ref's mutation. The callback is stable, so React never
   * detaches and reattaches on an ordinary re-render — including the re-render
   * that follows a reorder.
   */
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const setGridRef = useCallback((node: HTMLDivElement | null): void => {
    gridRef.current = node;
    setGridEl(node);
  }, []);
  const projectPresetRef = useRef<ProjectEmojiPreset | null | undefined>(projectPreset);
  projectPresetRef.current = projectPreset;

  // Batch upload queue (also used for single files — a batch of one).
  const [batch, setBatch] = useState<UploadItem<Draft>[] | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const queueRef = useRef<UploadQueue<Draft> | null>(null);
  const baseRef = useRef<StickerAsset[]>([]);
  const processing = batchRunning;

  // Emoji assignment / selection / single-sticker actions
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [actionId, setActionId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [presetManagerOpen, setPresetManagerOpen] = useState(false);
  const [rangeArm, setRangeArm] = useState(false);
  const selectModeRef = useRef(false);
  selectModeRef.current = selectMode;
  const rangeArmRef = useRef(false);
  rangeArmRef.current = rangeArm;
  const anchorRef = useRef<number | null>(null);

  const actionAsset = actionId ? assets.find((a) => a.id === actionId) ?? null : null;

  // ---- Emoji assignment ---------------------------------------------------
  const setStickerEmojis = (id: string, list: string[]): void => {
    onChange(assets.map((a) => (a.id === id ? setAssetEmojis(a, list) : a)));
  };

  /** One emoji → every selected sticker. */
  const applySameToSelected = (emoji: string): void => {
    const ids = new Set(selected);
    onChange(assets.map((a) => (ids.has(a.id) ? setAssetEmojis(a, [emoji]) : a)));
    toast.show(t('stickers.emojiUpdated'), 'success');
    setBulkOpen(false);
    exitSelect();
  };

  /** Repeat an emoji sequence cyclically across the selected stickers (visual order). */
  const applySequenceToSelected = (emojis: string[]): void => {
    onChange(assignSequenceToAssets(assets, selected, emojis));
    toast.show(t('stickers.emojiUpdated'), 'success');
    setBulkOpen(false);
    exitSelect();
  };

  const deleteSelected = (): void => {
    const ids = new Set(selected);
    onChange(reindex(assets.filter((a) => !ids.has(a.id))));
    toast.show(t('stickers.removed'), 'default');
    exitSelect();
  };

  const toggleSelected = useCallback((id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    hapticSelection();
  }, []);

  // Tap while in select mode: normal toggle, or (when range is armed) select the
  // inclusive range from the previous anchor to the tapped tile.
  const selectTap = useCallback(
    (index: number, id: string): void => {
      if (rangeArmRef.current && anchorRef.current !== null) {
        const a = Math.min(anchorRef.current, index);
        const b = Math.max(anchorRef.current, index);
        setSelected((prev) => {
          const next = new Set(prev);
          for (let i = a; i <= b; i += 1) next.add(assetsRef.current[i]?.id);
          return next;
        });
        anchorRef.current = null;
        setRangeArm(false);
        hapticSelection();
        return;
      }
      anchorRef.current = index;
      toggleSelected(id);
    },
    [toggleSelected],
  );
  const selectTapRef = useRef(selectTap);
  selectTapRef.current = selectTap;

  const exitSelect = (): void => {
    setSelectMode(false);
    setSelected(new Set());
    setRangeArm(false);
    anchorRef.current = null;
  };

  const deleteAsset = (id: string): void => {
    onChange(reindex(assets.filter((a) => a.id !== id)));
    setActionId(null);
    toast.show(t('stickers.removed'), 'default');
  };

  const startReplace = (id: string): void => {
    replaceTargetRef.current = id;
    replaceInputRef.current?.click();
  };

  const onReplaceFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const targetId = replaceTargetRef.current;
    replaceTargetRef.current = null;
    if (!file || !targetId) return;
    setActionId(null);
    try {
      const processed = await api.processSticker(file);
      onChange(
        assets.map((a) =>
          a.id === targetId
            ? {
                ...a,
                kind: processed.kind,
                // The asset reference is what persists; `src` is a signed URL
                // for rendering and is blanked again on save.
                media: processed.media,
                src: processed.src,
                format: processed.format,
                size: processed.size,
                width: processed.width,
                height: processed.height,
                durationMs: processed.durationMs,
                // Replaced media must be re-added on the next sync of a published pack.
                telegramFileId: undefined,
              }
            : a,
        ),
      );
      toast.show(t('stickers.replaced'), 'success');
    } catch (err) {
      /*
       * Same vocabulary as the batch queue, so replacing one sticker explains
       * itself exactly as a batch item does — "we couldn't read this image" for
       * a bad file, "try again" for a blip — rather than a generic failure.
       */
      const reason =
        err instanceof ApiClientError
          ? classifyMediaFailure({
              status: err.status,
              code: err.code,
              reason: err.details?.reason?.[0],
            })
          : 'INTERNAL';
      toast.show(t(mediaFailureCopyKey(reason) as TranslationKey), 'error');
    }
  };

  // Live values mirrored into refs so the global drag handlers stay stable.
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const overIndexRef = useRef<number | null>(null);

  const session = useRef<DragSession | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishRef = useRef<(commit: boolean) => void>(() => {});
  const detachRef = useRef<() => void>(() => {});

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overIndex, setOverIndexState] = useState<number | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const setOver = useCallback((index: number | null): void => {
    overIndexRef.current = index;
    setOverIndexState(index);
  }, []);

  const full = assets.length >= limit;

  // ---- Batch upload -------------------------------------------------------
  const pick = (): void => {
    if (full) {
      toast.show(t('stickers.limitReached'), 'error');
      return;
    }
    inputRef.current?.click();
  };

  /*
   * Rebuild the project assets from the pre-batch base plus every item that has
   * SUCCEEDED, in the user's original selection order — never in completion
   * order. Running on every state change makes it naturally idempotent: the
   * list is derived from the queue rather than appended to, so a retry that
   * finally succeeds slots the sticker into its rightful place instead of
   * adding a second one at the end, and a failure simply leaves a gap.
   */
  const commitBatch = useCallback((items: UploadItem<Draft>[]): void => {
    const done = items.filter((item) => item.status === 'ready' && item.result);
    let added: StickerAsset[] = done.map((item, idx) => ({
      ...(item.result as Draft),
      order: baseRef.current.length + idx,
    }));
    // If the project has an active default preset, batch-added stickers receive
    // the repeating sequence (starting from the first new sticker) instead of
    // the plain default. Individual stickers can still be overridden afterward.
    const preset = projectPresetRef.current;
    if (preset?.autoApply && preset.emojis.length > 0) {
      added = assignSequenceToAssets(added, added.map((a) => a.id), preset.emojis);
    }
    onChangeRef.current(reindex([...baseRef.current, ...added]));
  }, []);

  /**
   * One upload attempt.
   *
   * The draft's `id` is the QUEUE ITEM's id, so a sticker keeps one identity
   * across every attempt — paired with the server-side idempotency replay
   * (`uploadId`), a retry after a lost response can neither duplicate the
   * sticker nor mint a second asset.
   */
  const runUpload = useCallback(
    async (item: UploadItem<Draft>, signal: AbortSignal): Promise<Draft> => {
      const processed = await api.processSticker(item.file, { uploadId: item.id, signal });
      return {
        id: item.id,
        name: item.name,
        kind: processed.kind,
        media: processed.media,
        src: processed.src,
        format: processed.format,
        size: processed.size,
        width: processed.width,
        height: processed.height,
        durationMs: processed.durationMs,
        emojiList: [DEFAULT_STICKER_EMOJI],
        emoji: DEFAULT_STICKER_EMOJI,
        createdAt: new Date().toISOString(),
      };
    },
    [],
  );

  const drain = useCallback(
    async (queue: UploadQueue<Draft>): Promise<void> => {
      setBatchRunning(true);
      await queue.run();
      if (queue.cancelled) return;
      setBatchRunning(false);

      const items = queue.snapshot();
      const doneCount = items.filter((i) => i.status === 'ready').length;
      const failed = items.filter((i) => i.status === 'failed');
      if (failed.length === 0) {
        hapticImpact('light');
        toast.show(items.length === 1 ? t('stickers.added') : t('batch.added', { count: doneCount }), 'success');
        window.setTimeout(() => {
          queueRef.current = null;
          setBatch(null);
        }, 900);
      } else {
        /*
         * The toast names the single most common reason rather than a bare
         * count, so a batch that failed because the files are unreadable does
         * not read the same as one that failed because the network dropped.
         * Per-item reasons stay on the tiles.
         */
        toast.show(
          failed.length === 1
            ? t(mediaFailureCopyKey(failed[0].reason ?? 'INTERNAL') as TranslationKey)
            : t('batch.failed', { count: failed.length }),
          'error',
        );
      }
    },
    [t, toast],
  );

  const retryFailed = (): void => {
    const queue = queueRef.current;
    if (!queue) return;
    queue.retryFailed();
    void drain(queue);
  };

  const onFiles = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    const remaining = limit - assets.length;
    if (remaining <= 0) {
      toast.show(t('stickers.limitReached'), 'error');
      return;
    }

    // Capacity check — never silently exceed the pack limit.
    let accepted = files;
    if (files.length > remaining) {
      accepted = files.slice(0, remaining);
      toast.show(t('batch.capacity', { added: remaining }), 'default');
    }

    /*
     * A file whose type this project cannot accept never enters the queue —
     * there is nothing to retry about it, and letting it in would only give the
     * pipeline a job it is certain to fail.
     */
    const usable = accepted.filter((file) => kindForMime(file.type) !== null);
    if (usable.length < accepted.length) {
      toast.show(t('stickers.failure.unreadable'), 'error');
    }
    if (usable.length === 0) return;

    /*
     * Video is strictly serial and images run at the queue's default width. A
     * mixed selection takes the narrower of the two rather than trying to run
     * two pools, which is what the previous hand-rolled scheduler attempted —
     * and which could strand workers permanently when its per-kind gate blocked
     * the only remaining item.
     */
    const hasVideo = usable.some((file) => kindForMime(file.type) === 'video');
    baseRef.current = assets;
    queueRef.current?.cancel();
    const queue = new UploadQueue<Draft>({
      concurrency: hasVideo ? VID_CONCURRENCY : DEFAULT_UPLOAD_CONCURRENCY,
      maxAttempts: DEFAULT_MAX_ATTEMPTS,
      baseDelayMs: DEFAULT_BASE_DELAY_MS,
      backoffFactor: DEFAULT_BACKOFF_FACTOR,
      maxDelayMs: DEFAULT_MAX_DELAY_MS,
      run: runUpload,
      onChange: (items) => {
        setBatch(items);
        commitBatch(items);
      },
    });
    queue.enqueue(
      usable.map((file) => ({
        id: crypto.randomUUID(),
        name: file.name.replace(/\.[^.]+$/, '') || 'sticker',
        file,
      })),
    );
    queueRef.current = queue;
    await drain(queue);
  };

  // A queue must never outlive the component: an in-flight upload whose
  // response can no longer be applied is wasted work and a wasted request.
  useEffect(() => () => queueRef.current?.cancel(), []);

  // ---- Drag & drop reorder ------------------------------------------------
  /*
   * GESTURE OWNERSHIP (the whole reason this is hand-rolled).
   *
   * Safari resolves a touch's owner when the touch STARTS, from the effective
   * `touch-action` at that moment, and never reassigns it. A tile that begins
   * life scrollable stays scrollable for the rest of the gesture no matter what
   * classes, listeners or preventDefault calls appear afterwards — and once the
   * UI process has taken the gesture it stops delivering cancelable touchmoves
   * and sends pointercancel instead. So the tiles declare `touch-action: none`
   * up front and this component owns every touch that lands on one, from the
   * first event, with the touchmove listener attached to a node that outlives
   * the gesture.
   *
   * Owning the gesture means owning BOTH outcomes: a drag, and the ordinary
   * scroll the browser would otherwise have done. `scrolling` below is that
   * second half — the page is moved by hand, with a release fling, for the
   * gesture the user meant as a swipe.
   */
  const rectsRef = useRef<TileRect[]>([]);

  const measure = useCallback((): void => {
    const grid = gridRef.current;
    if (!grid) {
      rectsRef.current = [];
      return;
    }
    rectsRef.current = Array.from(grid.querySelectorAll<HTMLElement>('[data-tile-index]')).map(
      (tile) => {
        const rect = tile.getBoundingClientRect();
        return {
          index: Number(tile.dataset.tileIndex),
          left: rect.left,
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
        };
      },
    );
  }, []);

  /*
   * Rects and pointer coordinates are both VIEWPORT-relative, so they only stay
   * comparable while the page holds still. Every scroll — ours during a drag,
   * the fling, or anything else on the page — invalidates them, so re-measure
   * and recompute the target from the finger's unchanged viewport position.
   */
  const remeasureAndRetarget = useCallback((): void => {
    measure();
    if (session.current?.phase !== 'dragging') return;
    const over = nearestTileIndex(rectsRef.current, pointerRef.current.x, pointerRef.current.y);
    if (over !== null) setOver(over);
  }, [measure, setOver]);

  // ---- Hand-rolled scrolling ----------------------------------------------
  const flingRef = useRef<number | null>(null);
  /** Resolved once per gesture: the element this drag scrolls. */
  const scrollerRef = useRef<HTMLElement | null>(null);

  const scrollPos = useCallback(
    (): number => (scrollerRef.current ? scrollerRef.current.scrollTop : window.scrollY),
    [],
  );

  const scrollByPx = useCallback((dy: number): void => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop += dy;
    else window.scrollBy(0, dy);
  }, []);

  const stopFling = useCallback((): void => {
    if (flingRef.current !== null) {
      cancelAnimationFrame(flingRef.current);
      flingRef.current = null;
    }
  }, []);

  /** Momentum after a swipe, so hand-rolled scrolling still feels like scrolling. */
  const startFling = useCallback(
    (velocity: number): void => {
      if (Math.abs(velocity) < FLING_MIN_VELOCITY) return;
      let v = velocity;
      let last = performance.now();
      const step = (): void => {
        const now = performance.now();
        const dt = Math.min(32, now - last);
        last = now;
        scrollByPx(-v * dt);
        v *= FLING_DECAY ** dt;
        flingRef.current = Math.abs(v) > FLING_MIN_VELOCITY ? requestAnimationFrame(step) : null;
      };
      flingRef.current = requestAnimationFrame(step);
    },
    [scrollByPx],
  );

  // ---- Edge auto-scroll during a drag -------------------------------------
  const pointerRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  const stopAutoScroll = useCallback((): void => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /*
   * A target that is off-screen is otherwise unreachable — the finger cannot
   * leave the viewport, so "move this sticker ten rows down" would be
   * impossible. Near an edge the page follows the drag; because the content
   * moves under a stationary finger, the tiles are re-measured and the target
   * recomputed on every frame that actually scrolled.
   */
  const autoScrollTick = useCallback((): void => {
    rafRef.current = null;
    if (session.current?.phase !== 'dragging') return;

    const { y } = pointerRef.current;
    const height = window.innerHeight;
    let delta = 0;
    if (y < EDGE_PX) delta = -Math.ceil(((EDGE_PX - y) / EDGE_PX) * EDGE_MAX_STEP);
    else if (y > height - EDGE_PX) {
      delta = Math.ceil(((y - (height - EDGE_PX)) / EDGE_PX) * EDGE_MAX_STEP);
    }

    if (delta !== 0) {
      const before = scrollPos();
      scrollByPx(delta);
      if (scrollPos() !== before) remeasureAndRetarget();
    }
    rafRef.current = requestAnimationFrame(autoScrollTick);
  }, [remeasureAndRetarget, scrollByPx, scrollPos]);

  // ---- The gesture machine ------------------------------------------------
  const clearHold = useCallback((): void => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  /** The press has been held still long enough: this is a reorder. */
  const activate = useCallback((): void => {
    const s = session.current;
    holdTimer.current = null;
    if (!s || s.phase !== 'pending') return;
    if (selectModeRef.current) return; // in select mode, taps select — no drag
    s.phase = 'dragging';
    measure();
    setDraggingId(s.id);
    setOver(s.index);
    setGhost({ x: s.startX - s.grabDx, y: s.startY - s.grabDy, w: s.w, h: s.h });
    hapticSelection();
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(autoScrollTick);
  }, [autoScrollTick, measure, setOver]);

  const beginGesture = useCallback(
    (args: {
      id: string;
      index: number;
      pointerId: number | null;
      x: number;
      y: number;
      rect: DOMRect;
    }): void => {
      stopFling();
      const now = performance.now();
      session.current = {
        id: args.id,
        index: args.index,
        pointerId: args.pointerId,
        startX: args.x,
        startY: args.y,
        lastY: args.y,
        lastT: now,
        vy: 0,
        grabDx: args.x - args.rect.left,
        grabDy: args.y - args.rect.top,
        w: args.rect.width,
        h: args.rect.height,
        phase: 'pending',
      };
      pointerRef.current = { x: args.x, y: args.y };
      clearHold();
      holdTimer.current = setTimeout(activate, HOLD_MS);
    },
    [activate, clearHold, stopFling],
  );

  const moveGesture = useCallback(
    (x: number, y: number): void => {
      const s = session.current;
      if (!s) return;

      if (s.phase === 'pending') {
        if (Math.hypot(x - s.startX, y - s.startY) <= MOVE_CANCEL_PX) return;
        /*
         * Moved before the hold elapsed — the user is scrolling, not
         * reordering. A touch becomes a hand-rolled scroll (the browser will
         * not scroll this surface for us); a mouse simply abandons, exactly as
         * it always has.
         */
        clearHold();
        if (s.pointerId !== null) {
          finishRef.current(false);
          return;
        }
        s.phase = 'scrolling';
        s.lastY = y;
        s.lastT = performance.now();
      }

      if (s.phase === 'scrolling') {
        const dy = y - s.lastY;
        const now = performance.now();
        const dt = now - s.lastT;
        // Finger down-screen means content down-screen, i.e. scroll position up.
        if (dy !== 0) scrollByPx(-dy);
        if (dt > 0) s.vy = dy / dt;
        s.lastY = y;
        s.lastT = now;
        return;
      }

      pointerRef.current = { x, y };
      // Clamp the ghost inside the viewport so it can never cause overflow/scroll.
      const gx = Math.max(0, Math.min(x - s.grabDx, window.innerWidth - s.w));
      const gy = Math.max(0, Math.min(y - s.grabDy, window.innerHeight - s.h));
      setGhost({ x: gx, y: gy, w: s.w, h: s.h });
      const over = nearestTileIndex(rectsRef.current, x, y);
      if (over !== null && over !== overIndexRef.current) {
        setOver(over);
      }
    },
    [clearHold, scrollByPx, setOver],
  );

  const finish = useCallback(
    (commit: boolean): void => {
      const s = session.current;
      detachRef.current();
      clearHold();
      stopAutoScroll();

      const target = overIndexRef.current;
      if (s?.phase === 'dragging' && commit && target !== null && target !== s.index) {
        onChangeRef.current(reindex(moveItem(assetsRef.current, s.index, target)));
        hapticImpact('light');
      }
      // A quick press/release that never became a drag or a scroll is a tap.
      const tapId = s && s.phase === 'pending' && commit ? s.id : null;
      const tapIndex = s ? s.index : -1;
      const fling = s?.phase === 'scrolling' && commit ? s.vy : 0;

      session.current = null;
      setDraggingId(null);
      setOver(null);
      setGhost(null);
      if (fling !== 0) startFling(fling);
      if (tapId) {
        if (selectModeRef.current) selectTapRef.current(tapIndex, tapId);
        else setActionId(tapId);
      }
    },
    [clearHold, setOver, startFling, stopAutoScroll],
  );
  finishRef.current = finish;

  // ---- Touch path (phones, and therefore Telegram iOS) --------------------
  /*
   * Bound to the grid NODE and kept for its lifetime, non-passive. A listener
   * added inside pointerdown — as this used to do — is registered after WebKit
   * has already classified the touch, which is why the previous attempt still
   * lost vertical drags on iOS.
   */
  useEffect(() => {
    const grid = gridEl;
    if (!grid) return;

    const onTouchStart = (event: TouchEvent): void => {
      if (event.touches.length !== 1) {
        finishRef.current(false); // a second finger is never a reorder
        return;
      }
      const touch = event.touches[0];
      const target = event.target as HTMLElement | null;
      const tile = target?.closest?.('[data-tile-index]') as HTMLElement | null;
      // Gaps, the add button and anything else keep the browser's own scrolling.
      if (!tile || !touch) return;
      const index = Number(tile.dataset.tileIndex);
      const asset = assetsRef.current[index];
      if (!asset) return;
      beginGesture({
        id: asset.id,
        index,
        pointerId: null,
        x: touch.clientX,
        y: touch.clientY,
        rect: tile.getBoundingClientRect(),
      });
    };

    const onTouchMove = (event: TouchEvent): void => {
      const s = session.current;
      if (!s || s.pointerId !== null) return;
      /*
       * Safe to prevent, always: the tile declared `touch-action: none` before
       * the touch began, so there is no scroll in flight to fight — this only
       * stops selection, callouts and the double-tap zoom heuristics.
       */
      if (event.cancelable) event.preventDefault();
      const touch = event.touches[0];
      if (!touch) return;
      moveGesture(touch.clientX, touch.clientY);
    };

    const onTouchEnd = (): void => {
      if (session.current?.pointerId === null) finishRef.current(true);
    };

    const onTouchCancel = (): void => {
      // Should no longer happen on a tile; if it does, it is the signal that
      // something upstream took the gesture — never commit on it.
      if (session.current?.pointerId === null) finishRef.current(false);
    };

    grid.addEventListener('touchstart', onTouchStart, { passive: false });
    grid.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchCancel);
    return () => {
      grid.removeEventListener('touchstart', onTouchStart);
      grid.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [beginGesture, gridEl, moveGesture]);

  // ---- Pointer path (mouse / pen only) ------------------------------------
  const onPointerMove = useCallback(
    (event: globalThis.PointerEvent): void => {
      if (session.current?.pointerId !== event.pointerId) return;
      event.preventDefault();
      moveGesture(event.clientX, event.clientY);
    },
    [moveGesture],
  );
  const onPointerUp = useCallback((): void => {
    finishRef.current(true);
  }, []);
  const onPointerCancel = useCallback((): void => {
    finishRef.current(false);
  }, []);

  /*
   * Only the per-gesture pointer listeners. Scroll/resize invalidation is
   * registered once for the component's lifetime below — removing it here
   * would tear down that single registration after the first drag, since it is
   * the same function reference.
   */
  const detach = useCallback((): void => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
  }, [onPointerCancel, onPointerMove, onPointerUp]);
  detachRef.current = detach;

  const onTilePointerDown = (event: ReactPointerEvent<HTMLDivElement>, index: number, asset: StickerAsset): void => {
    // Touches are driven by the touch listeners above, which own the gesture
    // from the first event; handling them here as well would double-count.
    if (event.pointerType === 'touch') return;
    if (event.button != null && event.button !== 0) return;
    beginGesture({
      id: asset.id,
      index,
      pointerId: event.pointerId ?? 0,
      x: event.clientX,
      y: event.clientY,
      rect: (event.currentTarget as HTMLElement).getBoundingClientRect(),
    });
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
  };

  /*
   * Viewport-relative rects go stale the moment anything scrolls — our own
   * auto-scroll, the fling, or the page moving for any other reason. Registered
   * once, for both input paths, and capturing so a scroll on any ancestor
   * counts.
   */
  useEffect(() => {
    window.addEventListener('scroll', remeasureAndRetarget, true);
    window.addEventListener('resize', remeasureAndRetarget);
    return () => {
      window.removeEventListener('scroll', remeasureAndRetarget, true);
      window.removeEventListener('resize', remeasureAndRetarget);
    };
  }, [remeasureAndRetarget]);

  useEffect(() => {
    return () => {
      detachRef.current();
      stopAutoScroll();
      stopFling();
      if (holdTimer.current) clearTimeout(holdTimer.current);
    };
  }, [stopAutoScroll, stopFling]);

  const draggingAsset = draggingId ? assets.find((a) => a.id === draggingId) : null;

  /*
   * One line of plain-language status under the progress bar.
   *
   * Recovery outranks failure: while anything is retrying the user is told the
   * app is handling it, because that is the actionable truth — there is nothing
   * for them to do. Only once the queue has stopped does the failure reason
   * take over, and even then it is the shared, non-technical copy. No status
   * code, decoder message or stack ever reaches this string.
   */
  const batchStatusLine = ((): string | null => {
    if (!batch) return null;
    if (batch.some((item) => item.status === 'retrying')) {
      return t(MEDIA_RETRYING_COPY_KEY as TranslationKey);
    }
    if (batchRunning) return null;
    const reasons = batch.filter((i) => i.status === 'failed').map((i) => i.reason ?? 'INTERNAL');
    if (reasons.length === 0) return null;
    // When every failure shares a cause, say it; a mixed batch stays generic.
    const unique = new Set(reasons.map((r) => mediaFailureCopyKey(r)));
    return unique.size === 1 ? t([...unique][0] as TranslationKey) : null;
  })();

  return (
    <section className="stack" style={{ gap: 12 }}>
      <div className="row-between">
        <h2 className="screen__section-title" style={{ margin: 0 }}>
          {t('stickers.title')}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="muted" style={{ fontSize: 13 }}>
            {assets.length} / {limit}
          </span>
          {assets.length > 0 && (
            <button className="home__link" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}>
              {selectMode ? t('common.done') : t('stickers.select')}
            </button>
          )}
        </div>
      </div>

      {selectMode && (
        <div className="sticker-selectbar">
          <div className="sticker-selectbar__top">
            <span className="muted" style={{ fontSize: 13 }}>
              {t('stickers.selectedCount', { count: selected.size })}
            </span>
            <div className="sticker-selectbar__links">
              <button
                className="home__link"
                onClick={() =>
                  selected.size === assets.length
                    ? setSelected(new Set())
                    : setSelected(new Set(assets.map((a) => a.id)))
                }
              >
                {selected.size === assets.length ? t('stickers.deselectAll') : t('stickers.selectAll')}
              </button>
              <button
                className={`home__link${rangeArm ? ' home__link--active' : ''}`}
                onClick={() => {
                  setRangeArm((v) => !v);
                  anchorRef.current = null;
                }}
              >
                {rangeArm ? t('stickers.rangeArmed') : t('stickers.range')}
              </button>
            </div>
          </div>
          <div className="sticker-selectbar__actions">
            <Button size="sm" disabled={selected.size === 0} leftIcon={<Icon name="sparkles" size={16} />} onClick={() => setBulkOpen(true)}>
              {t('stickers.editEmoji')}
            </Button>
            <Button size="sm" variant="ghost" disabled={selected.size === 0} leftIcon={<Icon name="trash" size={16} />} onClick={deleteSelected}>
              {t('common.delete')}
            </Button>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptForType(stickerType)}
        multiple
        hidden
        onChange={(event) => void onFiles(event)}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept={acceptForType(stickerType)}
        hidden
        onChange={(event) => void onReplaceFile(event)}
      />

      {/* Compact batch progress */}
      {batch && batch.length > 1 && (
        <div className="batch-bar">
          <div className="batch-bar__row">
            <span className="batch-bar__label">
              {t('batch.progress', {
                done: batch.filter((i) => isSettled(i.status)).length,
                total: batch.length,
              })}
            </span>
            {!batchRunning && batch.some((i) => i.status === 'failed') && (
              <button className="batch-bar__retry" onClick={retryFailed}>
                {t('batch.retry', { count: batch.filter((i) => i.status === 'failed').length })}
              </button>
            )}
          </div>
          <div className="batch-bar__track">
            <span
              className="batch-bar__fill"
              style={{ width: `${(batch.filter((i) => isSettled(i.status)).length / batch.length) * 100}%` }}
            />
          </div>
          {/*
            The five states are distinguished, not collapsed: a queued item is
            waiting for a slot, a retrying one is recovering by itself, and only
            a `failed` one is the user's problem. Reporting "Retrying…" is what
            stops a transient blip from reading as a broken upload.
          */}
          <div className="batch-bar__dots">
            {batch.map((item) => (
              <span
                key={item.id}
                className={`batch-dot batch-dot--${item.status}`}
                title={
                  item.status === 'failed' && item.reason
                    ? `${item.name} — ${t(mediaFailureCopyKey(item.reason) as TranslationKey)}`
                    : item.name
                }
              />
            ))}
          </div>
          {batchStatusLine && <span className="batch-bar__note">{batchStatusLine}</span>}
        </div>
      )}

      {assets.length === 0 ? (
        <EmptyState
          icon={<Icon name="image" size={28} />}
          title={t('stickers.empty')}
          description={t('stickers.emptyHint')}
          action={
            <Button leftIcon={<Icon name="upload" size={18} />} loading={processing} onClick={pick}>
              {t('stickers.add')}
            </Button>
          }
        />
      ) : (
        <>
          <div ref={setGridRef} className={`sticker-grid${draggingId ? ' sticker-grid--dragging' : ''}`}>
            {assets.map((asset, index) => (
              <div
                key={asset.id}
                data-tile-index={index}
                className={`sticker-tile${asset.id === draggingId ? ' sticker-tile--dragging' : ''}${
                  overIndex === index && draggingId && asset.id !== draggingId ? ' sticker-tile--over' : ''
                }${selectMode ? ' sticker-tile--selectable' : ''}${
                  selected.has(asset.id) ? ' sticker-tile--selected' : ''
                }`}
                role="button"
                aria-label={`${t('stickers.title')} ${index + 1} ${asset.emoji}`}
                onPointerDown={(event) => onTilePointerDown(event, index, asset)}
              >
                {asset.kind === 'video' ? (
                  <video
                    className="sticker-tile__img"
                    src={asset.src}
                    muted
                    loop
                    autoPlay
                    playsInline
                    onError={onMediaError}
                  />
                ) : (
                  <img
                    className="sticker-tile__img"
                    src={asset.src}
                    alt={asset.name}
                    draggable={false}
                    onError={onMediaError}
                  />
                )}
                {asset.kind === 'video' && (
                  <span className="sticker-tile__kind">
                    <Icon name="image" size={12} /> WEBM
                  </span>
                )}
                <span className="sticker-tile__emoji">
                  {asset.emojiList[0]}
                  {asset.emojiList.length > 1 && (
                    <em className="sticker-tile__emoji-more">+{asset.emojiList.length - 1}</em>
                  )}
                </span>
                {selectMode && (
                  <span className="sticker-tile__check">
                    {selected.has(asset.id) && <Icon name="check" size={14} />}
                  </span>
                )}
              </div>
            ))}

            <button className="sticker-tile sticker-tile--add" onClick={pick} disabled={processing || full}>
              <Icon name={processing ? 'image' : 'upload'} size={24} />
              <span>{processing ? t('stickers.processing') : t('stickers.add')}</span>
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            {t('stickers.addHint')}
          </p>
        </>
      )}

      {ghost && draggingAsset && (
        <div className="sticker-ghost" style={{ left: ghost.x, top: ghost.y, width: ghost.w, height: ghost.h }}>
          {draggingAsset.kind === 'video' ? (
            <video className="sticker-tile__img" src={draggingAsset.src} muted playsInline />
          ) : (
            <img className="sticker-tile__img" src={draggingAsset.src} alt="" draggable={false} />
          )}
        </div>
      )}

      <StickerActionSheet
        asset={actionAsset}
        onClose={() => setActionId(null)}
        onSetEmojis={setStickerEmojis}
        onReplace={() => actionAsset && startReplace(actionAsset.id)}
        onDelete={() => actionAsset && deleteAsset(actionAsset.id)}
      />

      <BulkEmojiEditor
        open={bulkOpen}
        count={selected.size}
        onClose={() => setBulkOpen(false)}
        onSame={applySameToSelected}
        onSequence={applySequenceToSelected}
        onManagePresets={() => {
          setBulkOpen(false);
          setPresetManagerOpen(true);
        }}
      />

      <EmojiPresetManager open={presetManagerOpen} onClose={() => setPresetManagerOpen(false)} />
    </section>
  );
}
